import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Center,
    Flex,
    FormControl,
    FormErrorMessage,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    VStack
} from '@chakra-ui/react';
import { faEnvelope, faLock } from '@fortawesome/pro-duotone-svg-icons';
import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Auth } from 'aws-amplify';
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { FC, useState } from 'react';
import Link from 'src/components/common/link/Link';
import OneSignal from 'src/services/onesignal';
import { captureUserLoggedIn } from 'src/services/posthog/lifecycle-events';
import { addStoredSite } from 'src/utils/helpers/site-storage';
import { useAuth } from 'src/utils/hooks/useAuth';
import { useLanguage } from 'src/utils/hooks/useLanguage';
import { object, string } from 'yup';

const LoginForm: FC = () => {
    const { t } = useTranslation('login');
    const { language } = useLanguage();
    const { handleAuth, handleCachedAuth } = useAuth();
    const router = useRouter();
    const [errorMessage, setErrorMessage] = useState('');
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const validationSchema = object().shape({
        email: string().required(t('email-required')).email(t('email-invalid')),
        password: string().required(t('password-required'))
    });

    type FormValues = {
        email: string;
        password: string;
    };

    const initialValues: FormValues = { email: '', password: '' };

    const onSubmit = async (values: FormValues, actions: FormikHelpers<FormValues>) => {
        const { email, password } = values;
        try {
            const user = await Auth.signIn({
                username: email.toLowerCase(),
                password
            });
            handleAuth(user);

            addStoredSite({
                email: email.toLowerCase(),
                password
            });

            if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
                // not a completed login yet — ChangeTemporaryPasswordForm emits
                // user.logged_in {method:'temporary_password'} on completion
                handleCachedAuth(user);
                return router.push(`/${language}/change-temporary-password`);
            }
            captureUserLoggedIn({ method: 'password' });
            await OneSignal.enable(user.username);
            await OneSignal.setLanguage(language);
            return router.push(`/${language}/home`);
        } catch (error: any) {
            let message = t('generic-error-message');
            if (error.code === 'NotAuthorizedException') {
                message = t('not-authorized-error-message');
            }
            setErrorMessage(message);
        }
        actions.setSubmitting(false);
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}>
            {(props) => {
                const { values, isSubmitting } = props;

                return (
                    <Form>
                        <Flex
                            flexDirection="column"
                            height="calc(100vh - 144px)"
                            justifyContent="space-between"
                            paddingBottom={16}>
                            <VStack align="stretch" spacing={6}>
                                <Field id="email" name="email">
                                    {({ field, form }: FieldProps) => (
                                        <FormControl
                                            isInvalid={Boolean(
                                                form.errors.email && form.touched.email
                                            )}>
                                            <InputGroup>
                                                <InputLeftElement
                                                    color="lime.300"
                                                    height="100%"
                                                    pointerEvents="none"
                                                    width={16}>
                                                    <FontAwesomeIcon icon={faEnvelope} />
                                                </InputLeftElement>
                                                <Input
                                                    id="email"
                                                    paddingLeft={14}
                                                    placeholder={t('email-placeholder')}
                                                    type="email"
                                                    {...field}
                                                />
                                            </InputGroup>
                                            <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>
                                <Field id="password" name="password">
                                    {({ field, form }: FieldProps) => (
                                        <FormControl
                                            isInvalid={Boolean(
                                                form.errors.password && form.touched.password
                                            )}>
                                            <InputGroup>
                                                <InputLeftElement
                                                    alignItems="center"
                                                    color="lime.300"
                                                    display="flex"
                                                    height="100%"
                                                    justifyContent="center"
                                                    pointerEvents="none"
                                                    width={16}>
                                                    <FontAwesomeIcon icon={faLock} />
                                                </InputLeftElement>
                                                <Input
                                                    id="password"
                                                    paddingLeft={14}
                                                    placeholder={t('password-placeholder')}
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    {...field}
                                                />
                                                <InputRightElement
                                                    alignItems="center"
                                                    cursor="pointer"
                                                    display="flex"
                                                    height="100%"
                                                    justifyContent="center"
                                                    width={16}
                                                    onClick={() =>
                                                        setShowConfirmPassword(!showConfirmPassword)
                                                    }>
                                                    <FontAwesomeIcon
                                                        color="gray.800"
                                                        icon={
                                                            showConfirmPassword ? faEyeSlash : faEye
                                                        }
                                                    />
                                                </InputRightElement>
                                            </InputGroup>
                                            <FormErrorMessage>
                                                {form.errors.password}
                                            </FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>
                                <Box color="lime.500" textDecoration="underline" width="100%">
                                    <Center>
                                        <Link
                                            href={`/forgot-password?email=${encodeURIComponent(
                                                values.email.toLowerCase()
                                            )}`}>
                                            {t('forgot-password')}
                                        </Link>
                                    </Center>
                                </Box>

                                {errorMessage && (
                                    <Alert bg="purple.50" borderRadius="0.935rem" status="error">
                                        <AlertIcon color="purple.400" />
                                        {errorMessage}
                                    </Alert>
                                )}
                            </VStack>
                            <Button
                                colorScheme="lime"
                                disabled={isSubmitting}
                                isLoading={isSubmitting}
                                type="submit">
                                {t('login')}
                            </Button>
                        </Flex>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default LoginForm;
